import { useJpegDownloadSelectionStore } from '../../stores/useJpegDownloadSelectionStore';
import { buildSelectedJpegTargets } from './buildSelectedJpegTargets';
import { exportJpegTargets } from './exportJpegTargets';
import { DOWNLOAD_MESSAGES } from './downloadMessages';
import { getCurrentViewportImageId } from './getDisplaySetImageIds';
import { resolveStudyInstanceUIDFromViewport } from './resolveViewportStudy';
import type { DicomZipProgressCallback } from './types';

export type DownloadSelectedJpegOptions = {
  viewportId: string;
  servicesManager: AppTypes.ServicesManager;
  extensionManager: AppTypes.ExtensionManager;
  onProgress?: DicomZipProgressCallback;
};

export type DownloadSelectedJpegResult =
  | { success: true; filename: string; imageCount: number }
  | { success: false; message: string };

export async function downloadSelectedJpeg({
  viewportId,
  servicesManager,
  extensionManager,
  onProgress,
}: DownloadSelectedJpegOptions): Promise<DownloadSelectedJpegResult> {
  const { selectedImageIds, studyInstanceUID, clearSelection } =
    useJpegDownloadSelectionStore.getState();

  const resolvedStudyInstanceUID =
    studyInstanceUID ?? resolveStudyInstanceUIDFromViewport(viewportId, servicesManager);

  if (!resolvedStudyInstanceUID || !selectedImageIds.length) {
    return { success: false, message: DOWNLOAD_MESSAGES.NO_JPEG_SELECTED };
  }

  const targets = buildSelectedJpegTargets(
    resolvedStudyInstanceUID,
    selectedImageIds,
    servicesManager,
    extensionManager
  );

  if (!targets.length) {
    return { success: false, message: DOWNLOAD_MESSAGES.NO_JPEG_SELECTED };
  }

  const result = await exportJpegTargets(
    resolvedStudyInstanceUID,
    targets,
    extensionManager,
    onProgress,
    servicesManager
  );

  if (result.success) {
    clearSelection();
  }

  return result;
}

export function addCurrentViewportImageToSelection(
  viewportId: string,
  servicesManager: AppTypes.ServicesManager
): { success: boolean; message?: string } {
  const studyInstanceUID = resolveStudyInstanceUIDFromViewport(viewportId, servicesManager);
  const imageId = getCurrentViewportImageId(viewportId, servicesManager);

  if (!studyInstanceUID || !imageId) {
    return { success: false, message: DOWNLOAD_MESSAGES.NO_JPEG_SELECTED };
  }

  useJpegDownloadSelectionStore.getState().addImageId(imageId, studyInstanceUID);

  return { success: true };
}
